import React, { useState, useEffect } from 'react';
import { useSubscription, TIER_CONFIG, SUBSCRIPTION_TIERS } from '../../context/SubscriptionContext';
import { CreditCard, Lock, Check, ArrowLeft, AlertCircle, X, Crown, Sparkles, MapPin } from 'lucide-react';
import './Subscription.css';

const ALL_FEATURES = [
  { key: 'wizardA', label: 'Research Methodology Wizard (A)' },
  { key: 'wizardB', label: 'Data Analysis Planning Wizard (B)' },
  { key: 'wizardC', label: 'Framework Builder (C)' },
  { key: 'wizardD', label: 'Data Collection Process (D)' },
  { key: 'wizardE', label: 'Ethics Approval Wizard (E)' },
  { key: 'wizardF', label: 'Survey Builder & Qualitative Analysis (F)' },
  { key: 'learnBasics', label: 'Framework - Learn the Basics' },
  { key: 'compareMethodology', label: 'Compare Methodology' },
  { key: 'reportA', label: 'Report A - Methodology Report' },
  { key: 'reportB', label: 'Report B - Analysis Report' },
  { key: 'reportC', label: 'Report C - Framework Report' },
  { key: 'reportCombined', label: 'Combined Executive Report' }
];

const MockCheckout = ({ tier, onComplete, onCancel }) => {
  const { upgradeTier } = useSubscription();
  const [selectedTier, setSelectedTier] = useState(tier);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [userAddress, setUserAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });
  
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });
  
  const [sameAsUserAddress, setSameAsUserAddress] = useState(false);
  
  useEffect(() => {
    if (sameAsUserAddress) {
      setBillingAddress({ ...userAddress });
    }
  }, [sameAsUserAddress, userAddress]);

  const config = TIER_CONFIG[selectedTier];
  const canUpgrade = selectedTier !== SUBSCRIPTION_TIERS.ADVANCED && selectedTier !== SUBSCRIPTION_TIERS.SUPER;

  const hasFeature = (featureKey) => {
    const feature = config.features[featureKey];
    if (feature === true) return true;
    if (feature === false) return false;
    if (typeof feature === 'object' && feature.maxStep !== undefined) {
      return feature.maxStep >= 999;
    }
    return false;
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.slice(0, 19);
  };

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName || !email) {
      setError('Please fill in your personal details');
      return;
    }

    if (paymentMethod === 'stripe') {
      if (!cardholderName || !cardNumber || !expiry || !cvv) {
        setError('Please fill in all card details');
        return;
      }

      if (cardNumber.replace(/\s/g, '').length < 16) {
        setError('Please enter a valid card number');
        return;
      }
    }

    setProcessing(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    upgradeTier(selectedTier, email);
    setProcessing(false);
    setSuccess(true);

    setTimeout(() => {
      onComplete();
    }, 2500);
  };

  const handlePayPalClick = async () => {
    if (!firstName || !lastName || !email) {
      setError('Please fill in your personal details first');
      return;
    }
    setError('');
    setProcessing(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    upgradeTier(selectedTier, email);
    setProcessing(false);
    setSuccess(true);

    setTimeout(() => {
      onComplete();
    }, 2500);
  };

  if (success) {
    return (
      <div className="checkout-page">
        <div className="checkout-success">
          <div className="success-icon">
            <Check size={48} />
          </div>
          <h2>Payment Successful!</h2>
          <p>Welcome to {config.name}!</p>
          <p className="success-detail">Your subscription is now active.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <button className="checkout-back" onClick={onCancel}>
          <ArrowLeft size={20} />
          Back to Plans
        </button>

        <div className="checkout-content">
          <div className="checkout-summary">
            <h2>Order Summary</h2>
            <div className="summary-card">
              <div className="summary-tier">
                <span className="tier-name">{config.name}</span>
                {config.badge && <span className="tier-badge-small">{config.badge}</span>}
              </div>
              <div className="summary-price">
                {config.discount && (
                  <span className="original">{config.originalPrice}</span>
                )}
                <span className="final">{config.displayPrice}</span>
              </div>
              <p className="summary-description">{config.description}</p>
              
              <div className="summary-features-full">
                <h4>What You'll Get:</h4>
                <ul className="features-list">
                  {ALL_FEATURES.map((feature) => {
                    const included = hasFeature(feature.key);
                    return (
                      <li key={feature.key} className={included ? 'included' : 'excluded'}>
                        {included ? (
                          <Check size={16} className="feature-icon included" />
                        ) : (
                          <X size={16} className="feature-icon excluded" />
                        )}
                        <span>{feature.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {canUpgrade && (
              <div className="upsell-section">
                <div className="upsell-header">
                  <Sparkles size={20} />
                  <span>Last chance to get Full Access to everything!</span>
                </div>
                <p className="upsell-text">Unlock all wizards, reports, and premium features</p>
                <div className="upsell-toggle">
                  <button
                    type="button"
                    className={`upsell-btn ${selectedTier === SUBSCRIPTION_TIERS.ADVANCED ? 'active' : ''}`}
                    onClick={() => setSelectedTier(SUBSCRIPTION_TIERS.ADVANCED)}
                  >
                    <Crown size={16} />
                    <div className="upsell-btn-content">
                      <span className="upsell-tier-name">Advanced User</span>
                      <span className="upsell-tier-price">{TIER_CONFIG[SUBSCRIPTION_TIERS.ADVANCED].displayPrice}</span>
                    </div>
                    {TIER_CONFIG[SUBSCRIPTION_TIERS.ADVANCED].badge && (
                      <span className="upsell-badge popular">{TIER_CONFIG[SUBSCRIPTION_TIERS.ADVANCED].badge}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`upsell-btn ${selectedTier === SUBSCRIPTION_TIERS.SUPER ? 'active' : ''}`}
                    onClick={() => setSelectedTier(SUBSCRIPTION_TIERS.SUPER)}
                  >
                    <Sparkles size={16} />
                    <div className="upsell-btn-content">
                      <span className="upsell-tier-name">Super User</span>
                      <span className="upsell-tier-price">{TIER_CONFIG[SUBSCRIPTION_TIERS.SUPER].displayPrice}</span>
                    </div>
                    {TIER_CONFIG[SUBSCRIPTION_TIERS.SUPER].badge && (
                      <span className="upsell-badge value">{TIER_CONFIG[SUBSCRIPTION_TIERS.SUPER].badge}</span>
                    )}
                  </button>
                </div>
                {(selectedTier === SUBSCRIPTION_TIERS.ADVANCED || selectedTier === SUBSCRIPTION_TIERS.SUPER) && selectedTier !== tier && (
                  <button
                    type="button"
                    className="revert-btn"
                    onClick={() => setSelectedTier(tier)}
                  >
                    Keep original selection ({TIER_CONFIG[tier].name})
                  </button>
                )}
              </div>
            )}

            <div className="demo-warning">
              <AlertCircle size={16} />
              <span>Demo Mode: No real payment will be processed</span>
            </div>
          </div>

          <div className="checkout-form-container">
            <h2>Your Details</h2>
            
            <form className="checkout-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              <div className="address-section">
                <h3><MapPin size={18} /> User Address</h3>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={userAddress.street}
                    onChange={(e) => setUserAddress({ ...userAddress, street: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={userAddress.city}
                      onChange={(e) => setUserAddress({ ...userAddress, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State / Province</label>
                    <input
                      type="text"
                      value={userAddress.state}
                      onChange={(e) => setUserAddress({ ...userAddress, state: e.target.value })}
                      placeholder="State"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      value={userAddress.postalCode}
                      onChange={(e) => setUserAddress({ ...userAddress, postalCode: e.target.value })}
                      placeholder="12345"
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={userAddress.country}
                      onChange={(e) => setUserAddress({ ...userAddress, country: e.target.value })}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>

              <div className="address-section">
                <h3><MapPin size={18} /> Billing Address</h3>
                <div className="same-address-checkbox">
                  <input
                    type="checkbox"
                    id="sameAsUser"
                    checked={sameAsUserAddress}
                    onChange={(e) => setSameAsUserAddress(e.target.checked)}
                  />
                  <label htmlFor="sameAsUser">Same as User Address</label>
                </div>
                {!sameAsUserAddress && (
                  <>
                    <div className="form-group">
                      <label>Street Address</label>
                      <input
                        type="text"
                        value={billingAddress.street}
                        onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          value={billingAddress.city}
                          onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div className="form-group">
                        <label>State / Province</label>
                        <input
                          type="text"
                          value={billingAddress.state}
                          onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                          placeholder="State"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Postal Code</label>
                        <input
                          type="text"
                          value={billingAddress.postalCode}
                          onChange={(e) => setBillingAddress({ ...billingAddress, postalCode: e.target.value })}
                          placeholder="12345"
                        />
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          value={billingAddress.country}
                          onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="payment-method-toggle">
                <h3>Payment Method</h3>
                <div className="toggle-buttons">
                  <button
                    type="button"
                    className={`toggle-btn ${paymentMethod === 'stripe' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('stripe')}
                  >
                    <CreditCard size={18} />
                    Credit/Debit Card
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn paypal ${paymentMethod === 'paypal' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('paypal')}
                  >
                    <span className="paypal-logo">Pay<strong>Pal</strong></span>
                  </button>
                </div>
              </div>

              {paymentMethod === 'stripe' && (
                <>
                  <div className="form-group">
                    <label>Cardholder Name</label>
                    <input
                      type="text"
                      value={cardholderName}
                      onChange={(e) => setCardholderName(e.target.value)}
                      placeholder="Name on card"
                    />
                  </div>

                  <div className="form-group">
                    <label>Card Number</label>
                    <div className="card-input">
                      <CreditCard size={20} />
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiry Date</label>
                      <input
                        type="text"
                        value={expiry}
                        onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                    </div>
                    <div className="form-group">
                      <label>CVV</label>
                      <input
                        type="text"
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="123"
                        maxLength={4}
                      />
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="form-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {paymentMethod === 'stripe' ? (
                <button 
                  type="submit" 
                  className="checkout-submit"
                  disabled={processing}
                >
                  {processing ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Lock size={16} />
                      Pay {config.displayPrice}
                    </>
                  )}
                </button>
              ) : (
                <button 
                  type="button" 
                  className="checkout-submit paypal-submit"
                  disabled={processing}
                  onClick={handlePayPalClick}
                >
                  {processing ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <span className="paypal-logo-btn">Pay<strong>Pal</strong></span>
                      Checkout
                    </>
                  )}
                </button>
              )}

              <p className="secure-notice">
                <Lock size={14} />
                Secure payment - Demo mode
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockCheckout;
